import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { AUDIT_LOGS } from '@/data/mockData';
import { ShieldCheck } from 'lucide-react';
import { useSort } from '@/lib/useSort';
import { SortableHeader } from '@/components/shared/SortableHeader';
import { useDataLabel } from '@/lib/dataI18n';
import { useApp } from '@/context/AppContext';
import { adminApi } from '@/lib/adminApi';

export default function AdminAudit() {
  const { t } = useTranslation();
  const sort = useSort();
  const tData = useDataLabel();
  const { user } = useApp();
  const isAdvisor = user?.role === 'hm_advisor';
  const [bookNames, setBookNames] = useState(null); // null = not loaded, []=empty book

  // Iter 41.13 (Sesión D) — advisors only ever see audit rows tied to a
  // company in their assigned client book. Admins see everything.
  useEffect(() => {
    if (!isAdvisor) { setBookNames(null); return; }
    let alive = true;
    adminApi.listCompanies()
      .then((arr) => { if (alive) setBookNames(new Set((arr || []).map((c) => (c.name || '').trim().toLowerCase()))); })
      .catch(() => { if (alive) setBookNames(new Set()); });
    return () => { alive = false; };
  }, [isAdvisor]);

  const scopedLogs = useMemo(() => {
    if (!isAdvisor) return AUDIT_LOGS;
    if (bookNames === null) return [];                      // still loading
    return AUDIT_LOGS.filter((log) => bookNames.has((log.company || '').trim().toLowerCase()));
  }, [isAdvisor, bookNames]);

  const sorted = sort.apply(scopedLogs, {
    user: (a) => (a.user || '').toLowerCase(),
    action: (a) => (a.action || '').toLowerCase(),
    entity: (a) => (a.entity || '').toLowerCase(),
    ip: (a) => (a.ip || '').toLowerCase(),
    timestamp: (a) => a.timestamp ? new Date(a.timestamp).getTime() : 0,
  });
  return (
    <>
      <Topbar title={t('pages.audit.title')} subtitle={t('pages.audit.subtitle')} />
      <div className="p-6 lg:p-8">
        <Card className="shadow-card overflow-hidden">
          <div className="p-5 border-b border-border flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><ShieldCheck className="h-5 w-5" /></div>
            <div><h3 className="font-display font-bold text-lg tracking-tight">{t('pages.audit.subtitle')}</h3><p className="text-sm text-muted-foreground">{t('pages.audit.retentionNote')}</p></div>
          </div>
          <Table>
            <TableHeader className="bg-muted/40">
              <TableRow>
                <TableHead><SortableHeader sort={sort} k="user" testIdPrefix="audit">{t('pages.audit.th.user')}</SortableHeader></TableHead>
                <TableHead><SortableHeader sort={sort} k="action" testIdPrefix="audit">{t('pages.audit.th.action')}</SortableHeader></TableHead>
                <TableHead className="hidden md:table-cell"><SortableHeader sort={sort} k="entity" testIdPrefix="audit">{t('pages.audit.th.target')}</SortableHeader></TableHead>
                <TableHead className="hidden lg:table-cell"><SortableHeader sort={sort} k="ip" testIdPrefix="audit">{t('pages.audit.th.ip')}</SortableHeader></TableHead>
                <TableHead><SortableHeader sort={sort} k="timestamp" testIdPrefix="audit">{t('pages.audit.th.when')}</SortableHeader></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((log) => (
                <TableRow key={log.id} className="hover:bg-muted/30" data-testid={`audit-row-${log.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-7 w-7"><AvatarFallback className="bg-muted text-xs font-semibold">{log.user.split(' ').map(n=>n[0]).join('').slice(0,2)}</AvatarFallback></Avatar>
                      <span className="text-sm font-medium">{log.user}</span>
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="font-medium">{tData(log.action)}</Badge></TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{log.entity}</TableCell>
                  <TableCell className="hidden lg:table-cell text-xs font-mono text-muted-foreground tabular-nums">{log.ip}</TableCell>
                  <TableCell className="text-sm text-muted-foreground tabular-nums">{log.timestamp}</TableCell>
                </TableRow>
              ))}
              {isAdvisor && sorted.length === 0 && bookNames !== null && (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-12">
                  {t('pages.audit.empty', { defaultValue: 'No audit activity for your assigned clients yet.' })}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </>
  );
}
